/**
 * Date, time and duration formatting for the portal.
 *
 * Every helper takes the **UI locale** as a BCP-47 tag and the business's
 * **timezone** — two independent axes (doc 20 D5). An appointment at 14:30 in
 * `Europe/Warsaw` is 14:30 for the Polish owner and for their English-reading
 * accountant; only the words around it change.
 *
 * No helper defaults its locale. Passing `undefined` to `Intl` silently means
 * "the browser's locale", which is a setting the owner never chose and cannot
 * change from inside the app — the bug this module used to have.
 */

/** Format an instant as date + time in a given IANA timezone. */
export function formatDateTime(date: Date, timezone: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export function formatTime(date: Date, timezone: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/** Short calendar date in a timezone — "14 Jul", or "14 Jul 2025" outside the
 * current year (doc 12 §4). No time, no weekday. */
export function formatShortDate(date: Date, timezone: string, locale: string): string {
  const thisYear = new Date().getFullYear();
  const sameYear = new Intl.DateTimeFormat(locale, { timeZone: timezone, year: 'numeric' })
    .format(date) === String(thisYear);
  return new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    day: '2-digit',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(date);
}

/** "in 5 minutes" / "2 days ago", in the reader's language. */
export function formatRelative(date: Date, locale: string): string {
  const diffMs = date.getTime() - Date.now();
  const mins = Math.round(diffMs / 60000);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  if (Math.abs(mins) < 60) return rtf.format(mins, 'minute');
  const hours = Math.round(mins / 60);
  if (Math.abs(hours) < 24) return rtf.format(hours, 'hour');
  return rtf.format(Math.round(hours / 24), 'day');
}

/**
 * A call's length. The unit letters are the one piece of copy in here, so they
 * come from the caller rather than the catalogue — this module stays free of
 * the translator, and `Intl` has no duration formatter we can rely on across
 * the browsers we support.
 */
export function formatDurationSec(
  sec: number | undefined,
  units: { m: string; s: string } = { m: 'm', s: 's' },
): string {
  if (!sec) return '—';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}${units.m} ${s}${units.s}` : `${s}${units.s}`;
}
